import { useEffect, useRef } from "react";
import { eventListener, ITopicPayload } from "@origin-digital/event-dispatcher";

/**
 * Listens for events of type 'topic'. Once an event is heard will return the 'value', BUT if the value is undefined
 * will wait for the value to be defined and then return it via a promise resolver.
 *
 * @param topic The event type to listen for.
 * @param value The value to return to the event, if the value is undefined will store a promise resolver and return
 *              once the value changes to be defined.
 * @param timeout (optional) the timeout for the response. The promise will resolve to undefined after the timeout
 */
export const useRespondWhenDefined = <T extends ITopicPayload>(
  topic: T["topic"],
  value: Awaited<T["result"]> | undefined,
  timeout?: number
) => {
  const resolversQueue = useRef<((value: T["result"]) => void)[]>([]);
  const valueRef = useRef<Awaited<T["result"]> | undefined>(undefined);

  useEffect(() => {
    return eventListener.addListener(topic, async () => {
      if (valueRef.current) {
        return valueRef.current;
      } else {
        const resultPromise = new Promise((resolve) => {
          resolversQueue.current.push(resolve);
        });
        if (timeout) {
          const timeoutPromise = new Promise((resolve) =>
            setTimeout(resolve, timeout)
          );
          return Promise.race([resultPromise, timeoutPromise]);
        }
        return resultPromise;
      }
    });
  }, [timeout, topic]);

  useEffect(() => {
    valueRef.current = value;
    if (valueRef.current) {
      resolversQueue.current.forEach((resolver) => resolver(valueRef.current));
      resolversQueue.current = [];
    }
  }, [value]);
};
